import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createMatterStatuteOfLimitationsV3,
  deleteMatterStatuteOfLimitationsV1,
  updateMatterStatuteOfLimitationsV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterStatuteOfLimitations,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

type MatterStatuteOfLimitationsWithNewKeys = MatterStatuteOfLimitations & {
  id?: string;
};

type MatterWithStatutesOfLimitations = Matter & {
  matterStatuteOfLimitations?: MatterStatuteOfLimitationsWithNewKeys[];
  statutesOfLimitations?: MatterStatuteOfLimitationsWithNewKeys[];
};

const getMatterStatutesOfLimitations = (
  matter: MatterWithStatutesOfLimitations,
): MatterStatuteOfLimitationsWithNewKeys[] => {
  return matter.statutesOfLimitations;
};

const getStatuteOfLimitationsId = (
  statute: MatterStatuteOfLimitationsWithNewKeys,
) => statute.id;

const withStatutesOfLimitations = (
  matter: Matter,
  statutesOfLimitations: MatterStatuteOfLimitationsWithNewKeys[],
): MatterWithStatutesOfLimitations => ({
  ...(matter as MatterWithStatutesOfLimitations),
  matterStatuteOfLimitations: statutesOfLimitations,
  statutesOfLimitations,
});

export const useCreateMatterStatuteOfLimitations = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      data: Parameters<typeof createMatterStatuteOfLimitationsV3>[0],
    ) => createMatterStatuteOfLimitationsV3(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const statutesOfLimitations = getMatterStatutesOfLimitations(
            oldData.data as MatterWithStatutesOfLimitations,
          );

          return {
            ...oldData,
            data: withStatutesOfLimitations(oldData.data, [
              ...statutesOfLimitations,
              {
                ...data,
                id: "optimistic",
              } as MatterStatuteOfLimitationsWithNewKeys,
            ]),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error creating matter statute of limitations",
      );
    },
    onSettled: async (_data, _error, variables) => {
      if (!invalidateOnSettled) {
        return;
      }
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter statute of limitations created successfully");
      }
    },
  });
};

export const useUpdateMatterStatuteOfLimitations = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMatterStatuteOfLimitationsV1>[0],
    ) => updateMatterStatuteOfLimitationsV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const statutesOfLimitations = getMatterStatutesOfLimitations(
            oldData.data as MatterWithStatutesOfLimitations,
          );
          const nextStatutesOfLimitations = statutesOfLimitations.map(
            (statute) =>
              getStatuteOfLimitationsId(statute) === data.id
                ? { ...statute, ...data }
                : statute,
          );

          return {
            ...oldData,
            data: withStatutesOfLimitations(
              oldData.data,
              nextStatutesOfLimitations,
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error updating matter statute of limitations",
      );
    },
    onSettled: async (_data, _error, variables) => {
      if (!invalidateOnSettled) {
        return;
      }
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter statute of limitations updated successfully");
      }
    },
  });
};

export const useDeleteMatterStatuteOfLimitations = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      data: Parameters<typeof deleteMatterStatuteOfLimitationsV1>[0],
    ) => deleteMatterStatuteOfLimitationsV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const statutesOfLimitations = getMatterStatutesOfLimitations(
            oldData.data as MatterWithStatutesOfLimitations,
          );
          const nextStatutesOfLimitations = statutesOfLimitations.filter(
            (statute) => getStatuteOfLimitationsId(statute) !== data.id,
          );

          return {
            ...oldData,
            data: withStatutesOfLimitations(
              oldData.data,
              nextStatutesOfLimitations,
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error deleting matter statute of limitations",
      );
    },
    onSettled: async (_data, _error, variables) => {
      if (!invalidateOnSettled) {
        return;
      }
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Matter statute of limitations deleted successfully");
      }
    },
  });
};
